import { useEffect } from 'react';
import { Handle, Position, useUpdateNodeInternals } from '@xyflow/react';
import { FlowNodeBase, FlowNodePills } from './FlowNodeBase';
import { flowSourceHandlePosition, useFlowLayoutDirection } from './flow-layout-direction-context';

interface ApprovalDecisionOption {
  value: string;
  label: string;
}

interface FlowApprovalNodeData {
  label: string;
  status: string;
  config: {
    approvalType: string;
    decisionOptions?: ApprovalDecisionOption[];
  };
}

function getHandleColor(value: string): string {
  switch (value) {
    case 'approved':
    case 'needs_fix':
    case 'fix':
      return 'bg-status-success!';
    case 'rejected':
    case 'end_flow':
      return 'bg-status-error!';
    case 'ignore_in_sentry':
    case 'close_issue':
      return 'bg-status-accent-violet!';
    default:
      return 'bg-status-warning!';
  }
}

export function FlowApprovalNode({
  id,
  data,
  selected,
}: {
  id: string;
  data: FlowApprovalNodeData;
  selected?: boolean;
}) {
  const direction = useFlowLayoutDirection();
  const sourcePosition = flowSourceHandlePosition(direction);
  const isVertical = direction === 'TB';
  const decisionOptions = data.config.decisionOptions;

  // The graph reorders decisionOptions at display time to match the targets'
  // vertical order, which moves the handles. React Flow caches handle
  // positions, so it must be told to re-measure or edges keep attaching to
  // the old spots.
  const updateNodeInternals = useUpdateNodeInternals();
  const handleOrderKey = decisionOptions?.map((o) => o.value).join('|') ?? '';
  useEffect(() => {
    updateNodeInternals(id);
  }, [id, handleOrderKey, direction, updateNodeInternals]);
  const sourceHandles =
    decisionOptions && decisionOptions.length > 0 ? (
      <>
        {decisionOptions.map((option, index) => {
          const offset = `${((index + 1) / (decisionOptions.length + 1)) * 100}%`;
          return (
            <Handle
              key={option.value}
              id={option.value}
              type="source"
              position={sourcePosition}
              style={isVertical ? { left: offset } : { top: offset }}
              className={getHandleColor(option.value)}
            />
          );
        })}
      </>
    ) : (
      <>
        <Handle
          id="approved"
          type="source"
          position={sourcePosition}
          className="bg-status-success!"
        />
        <Handle
          id="rejected"
          type="source"
          position={isVertical ? Position.Right : Position.Top}
          className="bg-status-error!"
        />
      </>
    );

  return (
    <FlowNodeBase
      nodeType="approval"
      label={data.label}
      selected={selected}
      isActive={data.status === 'waiting_approval'}
      status={data.status}
      description={data.config.approvalType.replace(/_/g, ' ')}
      sourceHandles={sourceHandles}
    >
      {decisionOptions && decisionOptions.length > 0 ? (
        <FlowNodePills labels={decisionOptions.map((o) => o.label)} />
      ) : null}
    </FlowNodeBase>
  );
}
